import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { auth } from '../config/firebase';
import { fetchRuntimeConfig } from '../lib/runtimeConfig';
import { useAuth } from '../context/AuthContext';
import './Auth.css';
import './Checkout.css';

// API base once, at module level
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

// Lazy-load the Stripe instance once the publishable key is known
let stripePromise: Promise<Stripe | null> | null = null;

function getStripePromise(publishableKey: string) {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

/* ------------------------------------------------------------------ */
/*  Inner form rendered inside <Elements>                             */
/* ------------------------------------------------------------------ */
interface PriceInfo {
  amount: number;
  currency: string;
  interval: string;
  trialDays: number;
  firstBillingDate: string;
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
}

// Approximate per-day cost so the "~$X/day" callout tracks the real price.
function formatDailyPrice(amount: number, currency: string, interval: string): string {
  const daysInInterval = interval === 'year' ? 365 : interval === 'week' ? 7 : 30;
  const perDay = amount / 100 / daysInInterval;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(perDay);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function CheckoutForm({
  signupToken,
  mode,
  subscriptionId,
  priceInfo,
}: {
  signupToken: string;
  mode: 'signup' | 'reactivate';
  subscriptionId: string;
  priceInfo: PriceInfo | null;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const successPath = mode === 'reactivate' ? '/settings?subscription=reactivated' : '/survey';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setError(submitErr.message ?? 'Validation error');
      setProcessing(false);
      return;
    }

    const returnUrl = `${window.location.origin}${successPath}`;

    // Keep existing confirmSetup → confirmPayment fallback behavior
    const { error: confirmErr } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            email: user?.email || '',
            phone: user?.phoneNumber || '',
          },
        },
      },
      redirect: 'if_required',
    });

    if (confirmErr) {
      const { error: payErr } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      });
      if (payErr) {
        setError(payErr.message ?? 'Payment failed');
        setProcessing(false);
        return;
      }
    }

    // Verify subscription and activate user in the database
    try {
      if (mode === 'reactivate') {
        const idToken = await auth?.currentUser?.getIdToken();
        if (idToken) {
          await fetch(`${API_BASE}/subscription/verify-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ subscriptionId }),
          });
        }
      } else {
        await fetch(`${API_BASE}/signup/verify-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ signupToken, subscriptionId }),
        });
      }
    } catch (verifyErr) {
      console.warn('Post-checkout verify-subscription call failed:', verifyErr);
    }

    navigate(successPath);
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <PaymentElement options={{ fields: { billingDetails: { email: 'never', phone: 'never' } } }} />
      {error && <div className="auth-error">{error}</div>}
      <button type="submit" className="btn btn-primary btn-full" disabled={!stripe || processing}>
        {processing
          ? 'Processing…'
          : mode === 'reactivate'
          ? 'Reactivate Subscription'
          : 'Start Free Trial'}
      </button>
      {mode !== 'reactivate' && priceInfo && (
        <div className="checkout-price-summary">
          <p className="checkout-price-label">Founding Member Pricing</p>
          <p className="checkout-price">
            {priceInfo.trialDays}-day free trial, then {formatPrice(priceInfo.amount, priceInfo.currency)}/
            {priceInfo.interval}
          </p>
          <p className="checkout-price-callout">
            Just ~{formatDailyPrice(priceInfo.amount, priceInfo.currency, priceInfo.interval)}/day
          </p>
          <p className="checkout-price-lock">
            Lock in this rate as a founding member — your price stays here even when we raise it for new members.
          </p>
          <p className="checkout-billing-date">First billing date: {formatDate(priceInfo.firstBillingDate)}</p>
        </div>
      )}
      <p className="checkout-terms">
        {mode === 'reactivate' ? 'Your subscription will resume immediately.' : 'Cancel anytime in settings.'}
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Outer page: fetches clientSecret, renders Elements                */
/* ------------------------------------------------------------------ */
export default function Checkout() {
  const [searchParams] = useSearchParams();
  const signupToken = searchParams.get('t') ?? '';
  const mode = (searchParams.get('mode') === 'reactivate' ? 'reactivate' : 'signup') as 'signup' | 'reactivate';
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string>('');
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [priceInfo, setPriceInfo] = useState<PriceInfo | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!signupToken && mode === 'signup') {
      setError('Missing signup information. Please sign up again.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchRuntimeConfig();
        const pk = cfg?.stripePublishableKey;
        if (!pk) {
          setError('Payment system not configured.');
          setLoading(false);
          return;
        }
        if (!cancelled) setPublishableKey(pk);

        try {
          const priceRes = await fetch(`${API_BASE}/signup/price-info`);
          const priceData = await priceRes.json().catch(() => ({}));
          if (priceRes.ok && priceData.data && !cancelled) {
            setPriceInfo(priceData.data);
          }
        } catch (priceErr) {
          console.warn('Failed to fetch price info:', priceErr);
        }

        if (mode === 'reactivate') {
          const idToken = await auth?.currentUser?.getIdToken();
          if (!idToken) {
            setError('Please log in to reactivate your subscription.');
            setLoading(false);
            return;
          }
          const res = await fetch(`${API_BASE}/subscription/reactivate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({}),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            setError(data?.error || 'Failed to initialize payment.');
            setLoading(false);
            return;
          }
          if (!cancelled) {
            setSubscriptionId(data.data.subscriptionId || '');
            setClientSecret(data.data.clientSecret);
            setLoading(false);
          }
        } else {
          const res = await fetch(`${API_BASE}/signup/create-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ signupToken }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            setError(data?.error || 'Failed to initialize payment.');
            setLoading(false);
            return;
          }
          if (!cancelled) {
            setSubscriptionId(data.data.subscriptionId || '');
            setClientSecret(data.data.clientSecret);
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Checkout init error', err);
        if (!cancelled) {
          setError('Something went wrong. Please try again.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [signupToken, mode, navigate]);

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <p>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !clientSecret || !publishableKey) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <Link to="/" className="auth-logo">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img" />
            <span className="logo-text">mosaime</span>
          </Link>
          <div className="auth-card">
            <div className="auth-error">{error || 'Unable to load checkout.'}</div>
            <button
              className="btn btn-primary btn-full"
              onClick={() => navigate(mode === 'reactivate' ? '/settings' : '/signup')}
            >
              {mode === 'reactivate' ? 'Back to Settings' : 'Back to Signup'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Link to="/" className="auth-logo">
          <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img" />
          <span className="logo-text">mosaime</span>
        </Link>

        <div className="auth-card">
          <h1>{mode === 'reactivate' ? 'Reactivate Subscription' : 'Complete Your Signup'}</h1>
          <p className="auth-subtitle">
            {mode === 'reactivate'
              ? 'Enter your payment details to resume your wellness journey'
              : 'Enter your payment details to start your 7-day free trial'}
          </p>

          <Elements
            stripe={getStripePromise(publishableKey)}
            options={{
              clientSecret,
              appearance: { theme: 'stripe', variables: { colorPrimary: '#43A047' } },
            }}
          >
            <CheckoutForm
              signupToken={signupToken}
              mode={mode}
              subscriptionId={subscriptionId}
              priceInfo={priceInfo}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
}